import * as Sentry from '@sentry/vue';
import { OBI_SDK_URL_PATTERNS, shouldDropObiSdkSentryEvent } from '@/utilities/obiSdkNoise';
import { isCanceledRequest } from '@/api/requestCancellation';

/**
 * An axios failure with no response (XHR status 0). Offline it is the operator's
 * connection and not worth an event. Online it can be a CORS fault, a blocked
 * request or an outage only browsers can see, so it is reported. Handled
 * failures never get here: `processError` keeps ERR_NETWORK out of Sentry and
 * uses the same `navigator.onLine` test for its toast.
 */
function isOfflineNetworkError(event, hint) {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        return false;
    }

    const original = hint?.originalException;
    if (original?.code === 'ERR_NETWORK' || original?.message === 'Network Error') {
        return true;
    }

    return (event?.exception?.values ?? []).some((value) => value?.value === 'Network Error');
}

function createSentry({ app, router }) {
    // only run Sentry in deployed environments
    if (
        import.meta.env.VITE_ENV == 'production' ||
        import.meta.env.VITE_ENV == 'testing' ||
        import.meta.env.VITE_ENV == 'development'
    ) {
        let reportingEnvironment = import.meta.env.VITE_RELEASE_BRANCH ?? import.meta.env.VITE_ENV;

        if (reportingEnvironment == 'main') {
            reportingEnvironment = 'production';
        }

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            release: import.meta.env.VITE_RELEASE ? `movepro-crm@${import.meta.env.VITE_RELEASE}` : undefined,
            environment: reportingEnvironment,
            integrations: [
                Sentry.browserTracingIntegration({ router }),
                Sentry.replayIntegration(),
                // v11 defaults lifecycle to 'page' (one session per full load).
                // The CRM is an SPA; restore the v10-equivalent route-lifecycle sessions.
                Sentry.browserSessionIntegration({ lifecycle: 'route' }),
            ],

            // v11 defaults to 'stream', which sends every fetch span that starts
            // outside a pageload or navigation as its own segment. Each one is billed
            // against the shared performance reserve (CU-14zcqntjxek). 'static' keeps
            // the v10 model: fetch spans are only sent as children of a transaction.
            traceLifecycle: 'static',

            // v11 collects cookies, HTTP bodies, and user info by default.
            // CRM never set sendDefaultPii, so keep the restrictive v10 baseline.
            // Keys match the @sentry/core 11.0.0 runtime (urlQueryParams, not queryParams).
            // See https://docs.sentry.io/platforms/javascript/guides/vue/migration/v10-to-v11/
            dataCollection: {
                userInfo: false,
                cookies: false,
                httpHeaders: {
                    request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
                    response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
                },
                httpBodies: [],
                urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
                genAI: { inputs: false, outputs: false },
                databaseQueryData: false,
                queues: false,
                graphQL: { document: false, variables: false },
            },

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0.1,

            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            replaysSessionSampleRate: 0.01,
            replaysOnErrorSampleRate: 1.0,
            ignoreErrors: [
                'Non-Error exception captured',
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications',
                'Request failed with status code 422',
                // Google reCAPTCHA (VueReCaptcha, installed on every session) rejects
                // with the bare string 'Timeout' when a tab is backgrounded or slow.
                // Anchored: Sentry matches a string entry as a substring, which would
                // also drop 'Timeout exceeded' and any other rejection starting so.
                /^Non-Error promise rejection captured with value: Timeout$/,
            ],

            // Third-party Obi (Lisa) CDN — MOVEPRO-CRM-M5 / JB / HP keydown TypeError
            // when `e.key` is undefined. Sibling #1770 (CRM-M3) lazy-loads the SDK.
            denyUrls: OBI_SDK_URL_PATTERNS,
            beforeSend(event, hint) {
                // A request the CRM cancelled on purpose (route change, superseded
                // fetch), wherever it surfaced: Vue's error handler (MOVEPRO-CRM-DY),
                // unhandledrejection (MOVEPRO-CRM-K) or onerror.
                if (isCanceledRequest(hint?.originalException)) {
                    return null;
                }
                if (isOfflineNetworkError(event, hint)) {
                    return null;
                }
                if (shouldDropObiSdkSentryEvent(event, hint)) {
                    return null;
                }
                return event;
            },
        });
    }
}

export { createSentry };
